import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  TenantCompanyUser,
  TenantCompanyUserApiClient,
} from '@src/api/tenant/companyUser/tenantCompanyUserApiClient';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { useQueryParams } from '@src/hooks/useQueryParams';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import AuthIcon from '@src/ui/elements/table/AuthIcon';
import StatusTip from '@src/ui/elements/table/StatusTip';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title.tsx';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

const CompanyUserListForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<TenantCompanyUser[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    searchValue,
    setSearchValue,
    handleSort,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    getSearchParams,
    sort,
    page,
    keyword,
    direction,
  } = useQueryParams({});

  useEffect(() => {
    if (!searchParams.get('sort')) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('sort', 'created_at');
          newParams.set('direction', 'desc');
          return newParams;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const loadTenantUsers = async () => {
    const params = getSearchParams();
    setIsLoading(true);
    const res = await TenantCompanyUserApiClient.index(params);
    handleApiResponse(res, (data) => {
      setUsers(data.data);
      setPagination(data.pagination);
    });
    setIsLoading(false);
  };

  useEffect(() => {
    loadTenantUsers();
  }, [sort, keyword, direction, page]);

  return (
    <main className=''>
      <div>
        <SpinnerOverlay loading={isLoading} />
        <div className='flex flex-col'>
          <div className='flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>企業ユーザー一覧</Title>
            </div>
            <div className='w-[250px]'>
              <InputSearch
                value={searchValue}
                defaultValue={keyword || ''}
                onChange={(e) => setSearchValue(e.target.value)}
                onSubmit={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onClear={() => {
                  setSearchValue('');
                  setSearchParams(new URLSearchParams());
                }}
              />
            </div>
          </div>
        </div>
        {keyword && (
          <p className='overflow-wrap-anywhere mt-8 text-sm'>
            「{keyword}」で絞り込んだ結果：{pagination?.total}件
          </p>
        )}
        {!keyword && pagination?.total === 0 && (
          <p className='mt-4 text-sm'>データがありません。</p>
        )}
        <div className='mt-5 overflow-auto'>
          <table className='size-full min-w-[992px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[132px]' />
              <col className='w-[30%]' />
              <col className='w-[30%]' />
              <col className='w-[120px]' />
              <col className='w-2/5' />
              <col className='w-[120px]' />
              <col className='w-[120px]' />
              <col className='w-[140px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sort === 'created_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('created_at')}
                >
                  登録日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'fullname_kana' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('fullname_kana')}
                >
                  氏名
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'company.name_kana' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('company.name_kana')}
                >
                  企業名
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'companyRole.name' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('companyRole.name')}
                >
                  権限
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'email' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('email')}
                >
                  メールアドレス
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'enabled' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('enabled')}
                >
                  ステータス
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'expires_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('expires_at')}
                >
                  認証
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0
                ? users.map((user) => (
                    <tr
                      className='[&:nth-child(2n)]:bg-gray-10'
                      key={user.id}
                    >
                      <td>
                        <div className='truncate px-3 py-6'>{user.created_on}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>
                          {user.family_name} {user.first_name}
                        </div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{user.company_name}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{user.company_role_name}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{user.email}</div>
                      </td>
                      <td>
                        <div className='px-3 py-6'>
                          <StatusTip
                            label={user.status ? '有効' : '無効'}
                            active={user.status}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='px-3 py-6'>
                          <AuthIcon active={user.authenticated} />
                        </div>
                      </td>
                      <td>
                        <div className='flex justify-end px-4'>
                          <Button
                            size='s'
                            variant='quaternary'
                            outline
                            onClick={() =>
                              navigate(generatePath(TENANT_URL.COMPANY_USERS_EDIT, { id: user.id }))
                            }
                          >
                            編集する
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {users.length === 0 && !isLoading && <p className='mt-4 text-sm'>データがありません。</p>}
        </div>
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default CompanyUserListForm;
